import { Grid, TextField, Typography } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { CONSTANTS } from '../../../core/config/constants';
import { YearRangeWithOpenEndsFilter } from '../../../generated/model/yearRangeWithOpenEndsFilter';
import { useOnChange } from '../../../hooks/useOnChange';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';

interface Props {
  root?: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  field?: string;
  onChange: (state: YearRangeWithOpenEndsFilter) => void;
  defaultValue?: YearRangeWithOpenEndsFilter;
  active: boolean;
}

const YearRangeOpenEndedFilterInput: React.FC<Props> = React.memo(
  ({
    onChange,
    field,
    root,
    active,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: unknownSwitchEnabled === undefined ? true : unknownSwitchEnabled,
          value: { from: undefined, to: undefined },
        }
      : { value: { from: undefined, to: undefined } },
  }) => {
    const [state, setState] = useState<YearRangeWithOpenEndsFilter>(defaultValue);
    const [inputFromState, setInputFromState] = useState<number | undefined>(state?.value?.from);
    const [inputToState, setInputToState] = useState<number | undefined>(state?.value?.to);
    const TextInput = useMemo(
      () => (props: any) => <TextField margin="normal" variant="outlined" fullWidth {...props} />,
      [],
    );

    useOnChange(onChange, state, root);

    const rs: any[] = [];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }
    rs.push({
      label: 'Range:',
      value: `From ${state?.value?.from || 'Any year'} to ${state?.value?.to || 'Any year'}`,
    });

    function handleInputFromChange() {
      let from = inputFromState;

      if (
        typeof inputToState !== 'undefined' &&
        typeof inputFromState !== 'undefined' &&
        inputFromState > inputToState
      ) {
        from = inputToState;
      }

      setInputFromState(from);
      setState({ ...state, value: { from: from, to: inputToState } });
    }

    function handleInputToChange() {
      let to = inputToState;

      if (
        typeof inputFromState !== 'undefined' &&
        typeof inputToState !== 'undefined' &&
        inputToState < inputFromState
      ) {
        to = inputFromState;
      }

      setInputToState(to);
      setState({ ...state, value: { from: inputFromState, to: to } });
    }

    return (
      <>
        {active ? (
          <>
            <Grid container direction="column" id={field}>
              {withUnknownSwitch && (
                <Grid container direction="row" justify="space-between" alignItems="center">
                  <Grid item xs={12}>
                    <SwitchCompaniesUnknowns
                      onChange={(value) => {
                        setState({ ...state, includeEmpty: value });
                      }}
                      checked={state.includeEmpty}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container direction="row" justify="space-between" alignItems="center">
                <Grid item xs={5}>
                  <NumberFormat
                    decimalScale={0}
                    allowNegative={false}
                    value={inputFromState}
                    onValueChange={({ floatValue }) => setInputFromState(floatValue)}
                    onBlur={handleInputFromChange}
                    onKeyPress={(event) => {
                      if (CONSTANTS.ENTER_KEY_CODE === event.key) {
                        handleInputFromChange();
                      }
                    }}
                    customInput={TextInput}
                  />
                </Grid>
                <Grid>
                  <Typography>-</Typography>
                </Grid>
                <Grid item xs={5}>
                  <NumberFormat
                    decimalScale={0}
                    allowNegative={false}
                    value={inputToState}
                    onValueChange={({ floatValue }) => setInputToState(floatValue)}
                    onBlur={handleInputToChange}
                    onKeyPress={(event) => {
                      if (CONSTANTS.ENTER_KEY_CODE === event.key) {
                        handleInputToChange();
                      }
                    }}
                    customInput={TextInput}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

export default YearRangeOpenEndedFilterInput;
